// Copyright (c) Microsoft. All rights reserved.

import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { isAgentSessionActive } from '../../libs/models/ChatMessage';
import { BotResponseStatus, ChatState } from '../../redux/features/conversations/ChatState';
import { Breakpoints } from '../../styles';
import aiaIconAnimated from '../../assets/bot-icons/aia-icon-animated-transp.gif';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        borderRadius: tokens.borderRadiusMedium,
        ...Breakpoints.small({ maxWidth: '100%' }),
        gap: tokens.spacingHorizontalXS,
    },
    debug: {
        position: 'absolute',
        top: '-4px',
        right: '-4px',
    },
    alignEnd: {
        alignSelf: 'flex-end',
    },
    persona: {
        paddingTop: tokens.spacingVerticalS,
    },
    me: {
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    time: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        fontWeight: 400,
    },
    header: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalL,
    },
    canvas: {
        width: '100%',
        textAlign: 'center',
    },
    image: {
        maxWidth: '250px',
    },
    blur: {
        filter: 'blur(5px)',
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        marginTop: tokens.spacingVerticalS,
        marginBottom: tokens.spacingVerticalS,
        gap: tokens.spacingHorizontalL,
    },
    citationButton: {
        marginRight: 'auto',
    },
    rlhf: {
        marginLeft: 'auto',
    },
    content: {
        position: 'relative',
        gap: tokens.spacingHorizontalM,
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        fontWeight: 'bold',
    },
    newContent: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        fontWeight: 500,
        margin: 0,
    },
    thinkingWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXXS,
        backgroundColor: 'transparent',
        padding: 0,
    },
    thinkingIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tokens.colorNeutralForeground3,
    },
    typingIndicator: {
        display: 'inline-flex',
        alignItems: 'flex-end',
        gap: '4px',
        verticalAlign: 'baseline',
        transform: 'translateY(4px)',
    },
    typingDot: {
        width: '2.5px',
        height: '2.5px',
        borderRadius: '50%',
        backgroundColor: tokens.colorNeutralForeground3,
        animationDuration: '1.4s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'ease-in-out',
    },
    typingDot1: {
        animationName: {
            '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
            '30%': { transform: 'translateY(-8px)', opacity: 1 },
        },
    },
    typingDot2: {
        animationName: {
            '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
            '30%': { transform: 'translateY(-8px)', opacity: 1 },
        },
        animationDelay: '0.2s',
    },
    typingDot3: {
        animationName: {
            '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.4 },
            '30%': { transform: 'translateY(-8px)', opacity: 1 },
        },
        animationDelay: '0.4s',
    },
});

interface ChatStatusCardItemProps {
    statuses: BotResponseStatus[];
}

const ROTATION_INTERVAL_MS = 3500;

export const ChatStatusCard: React.FC<ChatStatusCardItemProps> = ({ statuses }) => {
    const classes = useClasses();
    const { t } = useTranslation();

    // The agent-typing indicator is no longer rendered here (#1933): it pins above the composer via
    // ChatStatus so it can't fall under the fold inside this scrollable card. This card now owns only
    // the AI statuses, and the agentSessionActive guard below still suppresses them while a human
    // handles the chat. The selector stays for that guard.
    //
    // Mutual exclusion with the agent-typing row used to be local (the old typing branch returned
    // before these AI-status checks); it is now cross-component. The invariant "never show an AI
    // status while 'Agenten skriver' is up" relies on the server short-circuiting the AI once an
    // agent handles the chat plus the agentSessionActive guard below — not on a local check. The one
    // narrow gap is post-ChatResolved: agentSessionActive has reopened but a stale typing ping can
    // linger up to TYPING_RECEIVER_TIMEOUT_MS, so a fresh AI status in that window would briefly show
    // alongside the pinned typing row. Accepted as narrow; if either side changes, add an explicit
    // isAgentTyping guard here to restore the local invariant.
    const { selectedId, conversations } = useAppSelector((state: RootState) => state.conversations);
    // The Conversations index signature lies about absence: a not-yet-loaded chat yields
    // undefined at runtime (same cast as ChatRoom's selectedConversation).
    const messages = (conversations[selectedId] as ChatState | undefined)?.messages;
    const agentSessionActive = React.useMemo(() => isAgentSessionActive(messages ?? []), [messages]);

    const lastStatus = statuses.length > 0 ? statuses[statuses.length - 1] : undefined;
    const lastStatusKey = lastStatus ? `${lastStatus.status}|${lastStatus.message ?? ''}` : '';

    const rotatingPhrases = React.useMemo(() => {
        const phrases = t('conversationStatusMessage.rotating', { returnObjects: true });
        return Array.isArray(phrases) ? (phrases as string[]) : [];
    }, [t]);

    const [rotationIndex, setRotationIndex] = React.useState(0);

    // Reset rotation when a new backend status arrives so it shows for a full tick
    // before the rotation resumes.
    React.useEffect(() => {
        setRotationIndex(0);
    }, [lastStatusKey]);

    // Depend on the primitive status/message values rather than the lastStatus object,
    // which gets a fresh reference on every parent re-render and would tear down the
    // interval before it can fire under streaming-chat load.
    const lastStatusName = lastStatus?.status;
    const lastStatusMessage = lastStatus?.message;

    React.useEffect(() => {
        if (!lastStatusName || lastStatusName === 'generateResponse' || lastStatusMessage) {
            return undefined;
        }
        if (rotatingPhrases.length === 0) {
            return undefined;
        }
        const handle = window.setInterval(() => {
            setRotationIndex((prev) => (prev + 1) % rotatingPhrases.length);
        }, ROTATION_INTERVAL_MS);
        return () => {
            window.clearInterval(handle);
        };
    }, [lastStatusName, lastStatusMessage, rotatingPhrases.length]);

    if (!lastStatus) {
        return null;
    }

    // No AI workflow statuses while a human agent handles the chat (#1770). The backend
    // short-circuits the AI then, but statuses still reach this card from the client side:
    // ChatRoom optimistically sets 'thinking' on send (skipped in agent mode, kept here as
    // the render-side guard), and a run that was already in flight when the agent joined
    // keeps streaming its statuses over SignalR. After ChatResolved the gate reopens and
    // the statuses come back with the AI.
    if (agentSessionActive) {
        return null;
    }

    // Hide status card while the bot is actively writing the answer
    if (lastStatus.status === 'generateResponse') {
        return null;
    }

    const resolveStatusText = (status: { status: string; message?: string | null }): string => {
        if (status.message) {
            return status.message;
        }
        if (rotatingPhrases.length > 0 && (status.status === 'thinking' || status.status === 'callingKernel')) {
            return rotatingPhrases[rotationIndex % rotatingPhrases.length];
        }
        return t('conversationStatusMessage.' + status.status);
    };

    return (
        <div className={classes.root}>
            <div className={classes.content}>
                <div className={classes.thinkingWrapper} aria-live="polite">
                    <span className={classes.thinkingIcon}>
                        <img src={aiaIconAnimated} alt="AIA Thinking" width={44} height={44} />
                    </span>
                    <p className={classes.newContent}>{resolveStatusText(lastStatus)}</p>
                    <div className={classes.typingIndicator} aria-label="Typing">
                        <span className={mergeClasses(classes.typingDot, classes.typingDot1)} />
                        <span className={mergeClasses(classes.typingDot, classes.typingDot2)} />
                        <span className={mergeClasses(classes.typingDot, classes.typingDot3)} />
                    </div>
                </div>
            </div>
        </div>
    );
};
